import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { setLoading } from 'redux/store/common/slice'
import useTabs, { TabsReturnActions, TabsReturnState } from 'hooks/useTabs'
import { getCCDetailsRequestAsync } from '../api'
import { contractCategorySubject } from '../Abilities'
import { getUser } from '../../../redux/store/user/getters'
import { useContractCategoryDetailsAbility } from './ContractCategoryDetailsAbilityProvider'
import { ROLES, SUBJECTS, ACTIONS } from '../../Permission'
import { CC_DETAILS_INITIAL } from './constants'
import {
  CONTRACT_CATEGORY_DETAILS_TABS,
  contractCategoryDetailsRoute
} from '../routes'
import { TContractCategoryDetails } from '../types'

type ContextProps = {
  state: {
    details: TContractCategoryDetails
    canEditContractCategory: boolean
    canManageHS: boolean
    isVendor: boolean
    isCommons: boolean
    isAllResponsibleUsersAdded: boolean
    isNewContractCategory: boolean
    canManageCCUsers: boolean
    canEditExpectedSpendAndSavings: boolean
    canViewExpectedSpendAndSavings: boolean
    canSeeHealthSystem: boolean
  } & TabsReturnState
  actions: {
    getCCDetailsAsync: () => Promise<void>
  } & TabsReturnActions
}

const ContractCategoryDetailsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const createRoute = 'create'

const prepareRouteId = (routeId?: string) =>
  routeId ? (routeId === createRoute ? null : routeId) : null

const ContractCategoryDetailsContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useDispatch()
  const { id: routeId, tab } = useParams()
  const user = useSelector(getUser)

  const isVendor = !!user.vendor
  const isCommons = user?.role.includes('commons')

  const [id, setId] = useState<string | null>(prepareRouteId(routeId))
  const [details, setDetails] =
    useState<TContractCategoryDetails>(CC_DETAILS_INITIAL)

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: CONTRACT_CATEGORY_DETAILS_TABS,
    activeTab: tab || CONTRACT_CATEGORY_DETAILS_TABS[0].key
  })

  const ability = useContractCategoryDetailsAbility()

  const canEditContractCategory = useMemo(
    () =>
      ability.can(
        ACTIONS.EDIT,
        contractCategorySubject(SUBJECTS.CONTRACT_CATEGORY, details)
      ),
    [ability, details]
  )
  const canManageHS = useMemo(
    () =>
      ability.can(
        ACTIONS.MANAGE,
        contractCategorySubject(SUBJECTS.HEALTH_SYSTEMS, details)
      ),
    [ability, details]
  )

  const canEditExpectedSpendAndSavings = useMemo(
    () =>
      ability.can(
        ACTIONS.EDIT,
        contractCategorySubject(SUBJECTS.EXPECTED_SAVINGS_SPEND, details)
      ),
    [ability, details]
  )

  const canSeeHealthSystem = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractCategorySubject(SUBJECTS.HEALTH_SYSTEM, user)
      ),
    [ability, user]
  )

  const canViewExpectedSpendAndSavings = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractCategorySubject(SUBJECTS.EXPECTED_SAVINGS_SPEND, details)
      ),
    [ability, details]
  )

  const isAllResponsibleUsersAdded = useMemo(
    () =>
      isCommons
        ? Boolean(
            details.commons_responsibles.find(
              (u) => u.role === ROLES.COMMONS_ANALYST
            ) &&
              details.commons_responsibles.find(
                (u) => u.role === ROLES.COMMONS_LEGAL
              )
          )
        : Boolean(
            details.community_responsibles.find(
              (u) => u.role === ROLES.ANALYST
            ) &&
              details.community_responsibles.find((u) => u.role === ROLES.LEGAL)
          ),
    [details.community_responsibles, isCommons, details.commons_responsibles]
  )

  const isNewContractCategory = routeId === 'new'

  const getCCDetailsAsync = useCallback(async () => {
    if (!id) {
      throw new Error('Contract Category ID not provided')
    }

    if (id === 'new') return

    try {
      dispatch(setLoading(true))

      const response = await getCCDetailsRequestAsync(id)

      if (response?.data) {
        setDetails(response.data)
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, id])

  const canManageCCUsers = useMemo(
    () =>
      ability.can(
        ACTIONS.MANAGE,
        contractCategorySubject(SUBJECTS.FACILITY_USERS, details)
      ),
    [ability, details]
  )

  const _adjustTabsAccessibility = useCallback(() => {
    tabsActions.setTabs(
      CONTRACT_CATEGORY_DETAILS_TABS.map((i) => {
        //TODO: implemented in facilities users feature. please don't touch it (sprint 16) :)
        const inCreationDisabled =
          i.key !== contractCategoryDetailsRoute.general &&
          isNewContractCategory

        return {
          ...i,
          disabled: inCreationDisabled
        }
      })
    )
  }, [tabsActions, isNewContractCategory])

  useLayoutEffect(() => {
    _adjustTabsAccessibility()
  }, [_adjustTabsAccessibility])

  useEffect(() => {
    if (!isNewContractCategory) {
      void getCCDetailsAsync()
    }
  }, [isNewContractCategory, id, getCCDetailsAsync])

  useEffect(() => {
    setId(prepareRouteId(routeId))
  }, [routeId])

  const context = useMemo(
    () => ({
      state: {
        details,
        canEditContractCategory,
        isAllResponsibleUsersAdded,
        isVendor,
        isNewContractCategory,
        isCommons,
        canManageHS,
        canManageCCUsers,
        canEditExpectedSpendAndSavings,
        canViewExpectedSpendAndSavings,
        canSeeHealthSystem,
        ...tabsState
      },
      actions: {
        ...tabsActions,
        getCCDetailsAsync
      }
    }),
    [
      details,
      tabsState,
      tabsActions,
      getCCDetailsAsync,
      canEditContractCategory,
      isVendor,
      isAllResponsibleUsersAdded,
      isNewContractCategory,
      isCommons,
      canManageHS,
      canManageCCUsers,
      canEditExpectedSpendAndSavings,
      canViewExpectedSpendAndSavings,
      canSeeHealthSystem
    ]
  )

  return (
    <ContractCategoryDetailsContext.Provider value={context}>
      {children}
    </ContractCategoryDetailsContext.Provider>
  )
}

export const useContractCategoryDetailsContext = () =>
  useContext(ContractCategoryDetailsContext)

export default ContractCategoryDetailsContextProvider
